import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type {
  InboundEmail,
  InboundExtraction,
  InboundInvoice,
  InboundSummary,
  InboundAttachment,
  Project,
} from '../types'
import { fmtDate, fmtMoney } from '../lib/format'

type Filter = 'unprocessed' | 'invoices' | 'bids' | 'cois' | 'all'

const DOC_TYPES: Array<{ id: string; label: string }> = [
  { id: 'unknown', label: 'Unknown' },
  { id: 'invoice', label: 'Invoice' },
  { id: 'bid', label: 'Bid' },
  { id: 'coi', label: 'COI' },
  { id: 'plan_revision', label: 'Plan revision' },
  { id: 'lien_waiver', label: 'Lien waiver' },
  { id: 'correspondence', label: 'Correspondence' },
]

export default function InboxTab() {
  const [emails, setEmails] = useState<InboundEmail[]>([])
  const [summary, setSummary] = useState<InboundSummary | null>(null)
  const [filter, setFilter] = useState<Filter>('unprocessed')
  const [selected, setSelected] = useState<InboundEmail | null>(null)
  const [invoice, setInvoice] = useState<InboundInvoice | null>(null)
  const [extraction, setExtraction] = useState<InboundExtraction | null>(null)
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [polling, setPolling] = useState(false)
  const [reprocessing, setReprocessing] = useState(false)
  const [pollMessage, setPollMessage] = useState<string | null>(null)

  async function refresh() {
    setLoading(true)
    setError(null)
    try {
      const params: Parameters<typeof api.inbound.list>[0] = {}
      if (filter === 'invoices') params.document_type = 'invoice'
      if (filter === 'bids') params.document_type = 'bid'
      if (filter === 'cois') params.document_type = 'coi'
      if (filter === 'unprocessed') params.status = 'new'
      const data = await api.inbound.list(params)
      setEmails(data.emails)
      setSummary(data.summary)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
  }, [filter])

  useEffect(() => {
    api.projects
      .list()
      .then(setProjects)
      .catch((e) => console.error('projects', e))
  }, [])

  async function openEmail(email: InboundEmail) {
    setSelected(email)
    setInvoice(null)
    setExtraction(null)
    try {
      const data = await api.inbound.get(email.id)
      setSelected(data.email)
      setInvoice(data.invoice)
      if (data.email.ai_extraction) {
        try {
          setExtraction(JSON.parse(data.email.ai_extraction) as InboundExtraction)
        } catch {
          setExtraction(null)
        }
      }
    } catch (e) {
      setError((e as Error).message)
    }
  }

  async function pollNow() {
    setPolling(true)
    setPollMessage(null)
    try {
      const result = await api.inbound.poll()
      setPollMessage(
        `Poll complete: ${result.fetched} fetched, ${result.inserted} new, ${result.skipped_duplicates} duplicates, ${result.ai_processed} AI-processed${result.ai_errors ? `, ${result.ai_errors} AI errors` : ''}`
      )
      await refresh()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setPolling(false)
    }
  }

  async function reprocess(id: string) {
    setReprocessing(true)
    try {
      await api.inbound.reprocess(id)
      await refresh()
      const data = await api.inbound.get(id)
      setSelected(data.email)
      setInvoice(data.invoice)
      if (data.email.ai_extraction) {
        setExtraction(JSON.parse(data.email.ai_extraction) as InboundExtraction)
      }
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setReprocessing(false)
    }
  }

  async function updateEmail(id: string, body: Partial<InboundEmail>) {
    try {
      const updated = await api.inbound.update(id, body)
      setEmails((prev) => prev.map((e) => (e.id === id ? { ...e, ...updated } : e)))
      if (selected?.id === id) setSelected({ ...selected, ...updated })
    } catch (e) {
      setError((e as Error).message)
    }
  }

  async function ignore(id: string) {
    if (!confirm('Mark this email as ignored?')) return
    await api.inbound.ignore(id)
    if (selected?.id === id) setSelected(null)
    await refresh()
  }

  async function fileToProject(id: string, projectId: string) {
    await updateEmail(id, { project_id: projectId, status: 'filed' })
    await refresh()
  }

  async function queueInvoiceForDraw(invoiceId: string) {
    try {
      await api.inbound.invoices.update(invoiceId, { draw_status: 'queued_for_draw' })
      const data = await api.inbound.get(selected!.id)
      setInvoice(data.invoice)
    } catch (e) {
      setError((e as Error).message)
    }
  }

  return (
    <section className="card">
      <div className="row" style={{ alignItems: 'baseline', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ margin: 0 }}>Inbox</h2>
        <span className="muted small">
          AI-triaged inbound email. Sync runs every 15 min.
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
          <button className="btn-primary" onClick={pollNow} disabled={polling}>
            {polling ? 'Syncing…' : '↻ Sync now'}
          </button>
        </div>
      </div>

      {pollMessage && <p className="muted small mt-2">{pollMessage}</p>}
      {error && <div className="card alert mt-2">{error}</div>}

      {/* Filter chips with counts */}
      <div className="row mt-2" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <FilterChip label={`Unprocessed${summary ? ` (${summary.new_count ?? 0})` : ''}`} active={filter === 'unprocessed'} onClick={() => setFilter('unprocessed')} />
        <FilterChip label={`Invoices${summary ? ` (${summary.invoices ?? 0})` : ''}`} active={filter === 'invoices'} onClick={() => setFilter('invoices')} />
        <FilterChip label={`Bids${summary ? ` (${summary.bids ?? 0})` : ''}`} active={filter === 'bids'} onClick={() => setFilter('bids')} />
        <FilterChip label={`COIs${summary ? ` (${summary.cois ?? 0})` : ''}`} active={filter === 'cois'} onClick={() => setFilter('cois')} />
        <FilterChip label={`All${summary ? ` (${summary.total ?? 0})` : ''}`} active={filter === 'all'} onClick={() => setFilter('all')} />
      </div>

      {loading ? (
        <p className="muted mt-2">Loading…</p>
      ) : emails.length === 0 ? (
        <p className="muted mt-2">
          {filter === 'unprocessed'
            ? 'Nothing waiting. New email gets here within 15 minutes of arriving in Gmail.'
            : 'No emails match this filter.'}
        </p>
      ) : (
        <div className="row mt-2" style={{ alignItems: 'flex-start', gap: '1rem' }}>
          <div style={{ flex: '1 1 320px', maxWidth: 480 }}>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {emails.map((e) => (
                <EmailRow
                  key={e.id}
                  email={e}
                  selected={selected?.id === e.id}
                  onClick={() => openEmail(e)}
                />
              ))}
            </ul>
          </div>
          <div style={{ flex: '2 1 480px' }}>
            {selected ? (
              <EmailDetail
                email={selected}
                invoice={invoice}
                extraction={extraction}
                projects={projects}
                reprocessing={reprocessing}
                onReprocess={() => reprocess(selected.id)}
                onIgnore={() => ignore(selected.id)}
                onFileToProject={(pid) => fileToProject(selected.id, pid)}
                onSetDocType={(t) => updateEmail(selected.id, { document_type: t as InboundEmail['document_type'] })}
                onQueueInvoiceForDraw={() => invoice && queueInvoiceForDraw(invoice.id)}
              />
            ) : (
              <p className="muted">Select an email to see details.</p>
            )}
          </div>
        </div>
      )}
    </section>
  )
}

function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      className="pill"
      onClick={onClick}
      style={{
        cursor: 'pointer',
        border: 'none',
        background: active ? 'rgba(212,175,55,0.35)' : 'rgba(255,255,255,0.06)',
        color: 'inherit',
        fontWeight: active ? 600 : 400,
      }}
    >
      {label}
    </button>
  )
}

function EmailRow({
  email,
  selected,
  onClick,
}: {
  email: InboundEmail
  selected: boolean
  onClick: () => void
}) {
  const docColor = docTypeColor(email.document_type)
  const matched = email.matched_sub_id || email.project_id
  return (
    <li
      onClick={onClick}
      style={{
        padding: '0.6rem 0.7rem',
        borderRadius: 6,
        cursor: 'pointer',
        background: selected ? 'rgba(212,175,55,0.10)' : matched ? 'rgba(46,160,67,0.06)' : 'rgba(220,180,40,0.06)',
        borderLeft: `3px solid ${matched ? '#2ea043' : '#d4af37'}`,
        marginBottom: 6,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
        <strong style={{ fontSize: '0.92rem' }}>{email.from_name ?? email.from_email}</strong>
        <span className="muted" style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
          {fmtDate(email.received_at)}
        </span>
      </div>
      <div style={{ fontSize: '0.88rem', marginTop: 2 }}>{email.subject ?? '(no subject)'}</div>
      <div className="row mt-1" style={{ gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <span
          style={{
            background: docColor,
            color: '#fff',
            fontSize: '0.7rem',
            padding: '1px 6px',
            borderRadius: 3,
            textTransform: 'uppercase',
          }}
        >
          {email.document_type}
        </span>
        {email.matched_sub_name && (
          <span className="muted small">→ {email.matched_sub_name}</span>
        )}
        {email.project_name && <span className="muted small">· {email.project_name}</span>}
        {email.status === 'new' && <span className="muted small">· NEW</span>}
        {email.ai_error && <span style={{ color: '#cc4444', fontSize: '0.78rem' }}>· AI error</span>}
      </div>
    </li>
  )
}

function EmailDetail({
  email,
  invoice,
  extraction,
  projects,
  reprocessing,
  onReprocess,
  onIgnore,
  onFileToProject,
  onSetDocType,
  onQueueInvoiceForDraw,
}: {
  email: InboundEmail
  invoice: InboundInvoice | null
  extraction: InboundExtraction | null
  projects: Project[]
  reprocessing: boolean
  onReprocess: () => void
  onIgnore: () => void
  onFileToProject: (id: string) => void
  onSetDocType: (t: string) => void
  onQueueInvoiceForDraw: () => void
}) {
  const attachments: InboundAttachment[] = email.attachments
    ? (JSON.parse(email.attachments) as InboundAttachment[])
    : []

  return (
    <div className="card" style={{ background: 'rgba(0,0,0,0.05)' }}>
      <div className="row" style={{ alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0 }}>{email.subject ?? '(no subject)'}</h3>
        <span className="muted small">{fmtDate(email.received_at)}</span>
      </div>
      <p className="muted small">
        From: {email.from_name ? `${email.from_name} <${email.from_email}>` : email.from_email}
        {email.matched_sub_name ? ` → matched ${email.matched_sub_name}` : ''}
      </p>

      {/* Action row */}
      <div className="row mt-2" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
        <select
          value={email.document_type}
          onChange={(e) => onSetDocType(e.target.value)}
          style={{ minWidth: 160 }}
        >
          {DOC_TYPES.map((d) => (
            <option key={d.id} value={d.id}>
              {d.label}
            </option>
          ))}
        </select>
        <select
          value={email.project_id ?? ''}
          onChange={(e) => onFileToProject(e.target.value)}
          style={{ minWidth: 200 }}
        >
          <option value="">— file to project —</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <button onClick={onReprocess} disabled={reprocessing}>
          {reprocessing ? 'Re-extracting…' : '↻ Re-extract'}
        </button>
        <button onClick={onIgnore}>✕ Ignore</button>
      </div>

      {/* AI extraction panel */}
      {extraction ? (
        <ExtractionPanel
          extraction={extraction}
          invoice={invoice}
          onQueueInvoiceForDraw={onQueueInvoiceForDraw}
        />
      ) : email.ai_error ? (
        <div className="card alert mt-2">
          <strong>AI error:</strong> {email.ai_error}
        </div>
      ) : (
        <p className="muted mt-2">AI hasn't processed this yet.</p>
      )}

      {/* Attachments */}
      {attachments.length > 0 && (
        <div className="mt-2">
          <h4>Attachments</h4>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {attachments.map((a) => (
              <li key={a.key} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                <a href={api.fileUrl(a.key)} target="_blank" rel="noreferrer">
                  📎 {a.filename}
                </a>
                <span className="muted small">
                  {a.mime} · {(a.size / 1024).toFixed(0)} KB
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Body preview */}
      {email.body_text && (
        <details className="mt-2">
          <summary className="muted small" style={{ cursor: 'pointer' }}>
            View email body
          </summary>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              fontSize: '0.85rem',
              padding: '0.5rem',
              background: 'rgba(255,255,255,0.04)',
              borderRadius: 4,
              maxHeight: 300,
              overflow: 'auto',
            }}
          >
            {email.body_text}
          </pre>
        </details>
      )}
    </div>
  )
}

function ExtractionPanel({
  extraction,
  invoice,
  onQueueInvoiceForDraw,
}: {
  extraction: InboundExtraction
  invoice: InboundInvoice | null
  onQueueInvoiceForDraw: () => void
}) {
  return (
    <div className="mt-2">
      <h4>
        AI Extraction · {extraction.document_type} ·{' '}
        <span className="muted small">{extraction.confidence} confidence</span>
      </h4>
      {extraction.reasoning && <p className="muted small">{extraction.reasoning}</p>}

      {extraction.invoice && (
        <div className="card" style={{ background: 'rgba(212,175,55,0.06)' }}>
          <div className="row" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
            <Field label="Vendor" value={extraction.invoice.vendor_name} />
            <Field label="Invoice #" value={extraction.invoice.invoice_number} />
            <Field label="Date" value={extraction.invoice.invoice_date} />
            <Field label="Due" value={extraction.invoice.due_date} />
            <Field
              label="Total"
              value={extraction.invoice.total_amount != null ? fmtMoney(extraction.invoice.total_amount) : null}
            />
          </div>
          {extraction.invoice.scope_description && (
            <p className="muted small mt-1">Scope: {extraction.invoice.scope_description}</p>
          )}
          {extraction.invoice.line_items && extraction.invoice.line_items.length > 0 && (
            <table className="mt-1" style={{ fontSize: '0.85rem', width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Qty</th>
                  <th>Unit</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {extraction.invoice.line_items.map((li, i) => (
                  <tr key={i}>
                    <td>{li.description}</td>
                    <td>{li.qty ?? '—'}</td>
                    <td>{li.unit_price != null ? fmtMoney(li.unit_price) : '—'}</td>
                    <td>{li.amount != null ? fmtMoney(li.amount) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {invoice && (
            <div className="row mt-2" style={{ alignItems: 'center', gap: '0.5rem' }}>
              <span className="muted small">
                Matched SCS line: {invoice.scs_item_label ?? '(none — assign manually)'}
                {invoice.line_number ? ` · #${invoice.line_number}` : ''}
              </span>
              <span className="muted small">· Status: {invoice.draw_status}</span>
              {invoice.draw_status === 'unfiled' && (
                <button
                  className="btn-primary"
                  onClick={onQueueInvoiceForDraw}
                  style={{ marginLeft: 'auto' }}
                >
                  Queue for next draw
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {extraction.bid && (
        <div className="card" style={{ background: 'rgba(212,175,55,0.06)' }}>
          <div className="row" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
            <Field label="Vendor" value={extraction.bid.vendor_name} />
            <Field label="Trade" value={extraction.bid.trade} />
            <Field
              label="Total"
              value={extraction.bid.total_amount != null ? fmtMoney(extraction.bid.total_amount) : null}
            />
            <Field label="Valid through" value={extraction.bid.valid_through} />
            <Field label="Payment terms" value={extraction.bid.payment_terms} />
          </div>
          {extraction.bid.included_scope?.length > 0 && (
            <details className="mt-1">
              <summary className="muted small" style={{ cursor: 'pointer' }}>
                Included scope ({extraction.bid.included_scope.length})
              </summary>
              <ul>
                {extraction.bid.included_scope.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </details>
          )}
          {extraction.bid.exclusions?.length > 0 && (
            <details className="mt-1">
              <summary className="muted small" style={{ cursor: 'pointer' }}>
                Exclusions ({extraction.bid.exclusions.length})
              </summary>
              <ul>
                {extraction.bid.exclusions.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {extraction.coi && (
        <div className="card" style={{ background: 'rgba(212,175,55,0.06)' }}>
          <div className="row" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
            <Field label="Insured" value={extraction.coi.insured_name} />
            <Field label="Carrier" value={extraction.coi.carrier} />
            <Field label="Policy #" value={extraction.coi.policy_number} />
            <Field label="Effective" value={extraction.coi.effective_date} />
            <Field label="Expires" value={extraction.coi.expiration_date} />
            <Field
              label="GL each occ"
              value={
                extraction.coi.general_liability_each_occurrence != null
                  ? fmtMoney(extraction.coi.general_liability_each_occurrence)
                  : null
              }
            />
            <Field
              label="GL aggregate"
              value={
                extraction.coi.general_liability_aggregate != null
                  ? fmtMoney(extraction.coi.general_liability_aggregate)
                  : null
              }
            />
            <Field
              label="Workers comp"
              value={extraction.coi.workers_comp == null ? null : extraction.coi.workers_comp ? 'yes' : 'no'}
            />
            <Field
              label="Heritage Add'l Insured"
              value={
                extraction.coi.additional_insured_listed == null
                  ? null
                  : extraction.coi.additional_insured_listed
                  ? 'yes'
                  : 'NO — request endorsement'
              }
            />
          </div>
        </div>
      )}

      {extraction.lien_waiver && (
        <div className="card" style={{ background: 'rgba(212,175,55,0.06)' }}>
          <div className="row" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
            <Field label="Type" value={extraction.lien_waiver.waiver_type} />
            <Field label="Payee" value={extraction.lien_waiver.payee} />
            <Field
              label="Amount"
              value={
                extraction.lien_waiver.amount != null ? fmtMoney(extraction.lien_waiver.amount) : null
              }
            />
            <Field label="Through" value={extraction.lien_waiver.through_date} />
          </div>
        </div>
      )}

      {extraction.plan_revision && (
        <div className="card" style={{ background: 'rgba(212,175,55,0.06)' }}>
          <Field label="Architect" value={extraction.plan_revision.architect} />
          <Field label="Revision" value={extraction.plan_revision.revision_label} />
          <Field label="Date" value={extraction.plan_revision.revision_date} />
          {extraction.plan_revision.sheets_affected?.length > 0 && (
            <p className="small">
              Sheets affected: {extraction.plan_revision.sheets_affected.join(', ')}
            </p>
          )}
          <p className="small">{extraction.plan_revision.summary}</p>
        </div>
      )}

      {extraction.correspondence && (
        <div className="card" style={{ background: 'rgba(212,175,55,0.06)' }}>
          <p>{extraction.correspondence.summary}</p>
          {extraction.correspondence.action_required && (
            <p style={{ color: '#d4af37' }}>
              <strong>Action required:</strong> {extraction.correspondence.action_summary}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function Field({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div style={{ minWidth: 130 }}>
      <div className="muted small" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </div>
      <div style={{ fontSize: '0.92rem' }}>{value == null || value === '' ? '—' : value}</div>
    </div>
  )
}

function docTypeColor(t: string): string {
  switch (t) {
    case 'invoice':
      return '#1f6feb'
    case 'bid':
      return '#7c3aed'
    case 'coi':
      return '#0c8d6f'
    case 'plan_revision':
      return '#b45309'
    case 'lien_waiver':
      return '#475569'
    case 'correspondence':
      return '#525252'
    default:
      return '#94703a'
  }
}
